// @ts-strict-ignore
import { deleteProperty, getProperty, setProperty } from 'dot-prop';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import compact from 'lodash/compact';
import { getNumericPathIndices } from 'flow/service/actions';
import { booleanRuleStrings } from '../../constants';
import type {
  BooleanRule,
  BooleanRuleArray,
  IndexedRule,
  Rules,
} from '../../types';
import { RuleUpdateErrorType } from '../constants';
import {
  buildActionRuleThresholdOperationMsg,
  buildRuleOperatorChangeOperationMsg,
  getOperatorUsedInRuleObject,
  getPathArrayAndParentPathString,
} from '../helpers';
import { NewLineInsertionPosition } from '~/dashboard/views/workflows/store/engine/update/rules/constants';
import { checkIfRuleHasAmbiguousKey, correctAmbiguousBooleanRuleObject } from '~/dashboard/views/workflows/service/thresholds';
import type { WorkflowState } from 'flow/store/redux/state';
import { isBooleanRule } from 'flow/service/rules';

function cleanRuleArray(
  rules: (BooleanRule | IndexedRule)[],
) {
  return rules.filter(rule => {
    if (rule && rule !== undefined && rule !== null && typeof rule !== undefined) {
      let ruleOperator;
      if ('and' in rule) {
        ruleOperator = 'and';
      }

      if ('or' in rule) {
        ruleOperator = 'or';
      }

      if (ruleOperator) {
        if ('ambiguous' in rule) delete rule.ambiguous;
        return cleanRulesWithTopLevelOperator(rule[ruleOperator]);
      }

      return rule;
    }

    return false;
  });
}

export function cleanRulesWithTopLevelOperator(
  rules: {} | IndexedRule | BooleanRule |
  BooleanRule[] | IndexedRule[] | (BooleanRule | IndexedRule)[],
) {
  let topLevelOperator;
  if ('and' in rules) {
    topLevelOperator = 'and';
  }

  if ('or' in rules) {
    topLevelOperator = 'or';
  }

  if (topLevelOperator) {
    rules[topLevelOperator] = cleanRuleArray(rules[topLevelOperator]);
  } else {
    return cleanRuleArray(rules as (BooleanRule | IndexedRule)[]);
  }

  if ('ambiguous' in rules) delete rules.ambiguous;

  return rules;
}

export function findThresholdIdAndUpdate({
  thresholdId,
  booleanRule,
}) {
  return booleanRule.reduce((result, ruleObject) => {
    if (ruleObject.id && ruleObject.id !== thresholdId) {
      result.push(ruleObject);
    }

    const firstOperatorKeyInTheOriginalRule = ruleObject.and
      ? booleanRuleStrings.AND_OPERATOR_STRING
      : booleanRuleStrings.OR_OPERATOR_STRING;
    if (ruleObject[firstOperatorKeyInTheOriginalRule]) {
      const updatedRule = findThresholdIdAndUpdate({
        thresholdId,
        booleanRule: ruleObject[firstOperatorKeyInTheOriginalRule],
      });

      if (updatedRule.length > 0) {
        result.push({
          [firstOperatorKeyInTheOriginalRule]: updatedRule,
          publicId: isBooleanRule(ruleObject) ? ruleObject.publicId : undefined,
        });
      }
    }

    return result;
  }, []);
}

export function addThresholdToBooleanRule({
  featureFlags,
  thresholdId,
  pathToPerformActionAt,
  originalRule,
}: {
  featureFlags: WorkflowState['featureFlagsForStore'];
  thresholdId: string;
  pathToPerformActionAt: string;
  originalRule: Rules | {};
}) {
  const booleanRule = cloneDeep(originalRule);
  if (typeof booleanRule === 'string') return booleanRule;
  if (!('id' in booleanRule)
    && !('and' in booleanRule) && !('or' in booleanRule)) {
    // no rules at all
    return featureFlags.ruleBuilderRefactor
      ? {
        [booleanRuleStrings.AND_OPERATOR_STRING]: [
          { id: thresholdId },
        ],
      }
      : { id: thresholdId };
  }

  if ('id' in booleanRule) {
    // this was a simple flat indexed rule with 1 threshold
    const operator = pathToPerformActionAt === booleanRuleStrings.OR_OPERATOR_STRING
      ? booleanRuleStrings.OR_OPERATOR_STRING
      : booleanRuleStrings.AND_OPERATOR_STRING;
    return {
      [operator]: [
        {
          id: booleanRule.id,
        },
        {
          id: thresholdId,
        },
      ],
    };
  }

  const firstOperatorKeyInTheOriginalRule = 'and' in booleanRule
    ? booleanRuleStrings.AND_OPERATOR_STRING
    : booleanRuleStrings.OR_OPERATOR_STRING;
  // otherwise it has more than one threshold in the rule
  if (pathToPerformActionAt?.includes('[')) {
    // event was sent to a path that's more than simply and/or operator key,
    // meaning this threshold would have to be attached to a specific key value pair
    // path would be like this: sample 1- and[1] / sample 2- or[2].and
    const getPathData = getProperty(
      booleanRule,
      pathToPerformActionAt,
    ) as {
      id: string;
    }[];

    if (!getPathData?.length) {
      return undefined;
    }

    const updatedRule = setProperty(
      booleanRule,
      pathToPerformActionAt,
      [
        ...getPathData,
        {
          id: thresholdId,
        },
      ],
    );

    return updatedRule;
  }

  // we by default attach the new threshold onto the top level key unless path is specified
  return {
    [firstOperatorKeyInTheOriginalRule]: [
      ...booleanRule[firstOperatorKeyInTheOriginalRule],
      {
        id: thresholdId,
      },
    ],
  };
}

export function deleteThresholdFromBooleanRule({
  thresholdId,
  originalRule,
}: {
  thresholdId: string;
  originalRule: Rules | {};
}) {
  const booleanRule = cloneDeep(originalRule);
  if (typeof booleanRule === 'string') return booleanRule;
  if ('id' in booleanRule) {
    // this was a simple flat indexed rule with 1 threshold
    return {};
  }

  const firstOperatorKeyInTheOriginalRule = 'and' in booleanRule
    ? booleanRuleStrings.AND_OPERATOR_STRING
    : booleanRuleStrings.OR_OPERATOR_STRING;
  // for removal we use recursive function to remove and also correctly reformat objects
  // if the threshold id being deleted was part of another and/or array and
  // after deletion there's only one element left in that array
  const updatedRule = Object.values(booleanRule).length > 0
    ? findThresholdIdAndUpdate({
      thresholdId,
      booleanRule: booleanRule[firstOperatorKeyInTheOriginalRule],
    })
    : {};

  if (updatedRule.length) {
    return {
      [firstOperatorKeyInTheOriginalRule]: updatedRule,
      publicId: isBooleanRule(booleanRule) ? booleanRule.publicId : undefined,
    };
  }

  return {};
}

function changeOperatorInRuleObject({
  newRuleOperator,
  pathToPerformActionAt,
  booleanRule,
}: {
  newRuleOperator: 'and' | 'or';
  pathToPerformActionAt: string;
  booleanRule: BooleanRule | BooleanRuleArray;
}) {
  const pathData = getProperty(
    booleanRule,
    pathToPerformActionAt,
  ) as {
    id: string;
  }[];

  const {
    parentPathString,
  } = getPathArrayAndParentPathString({
    pathString: pathToPerformActionAt.slice(),
    parentLevel: 1,
  });

  if (parentPathString.length === 0) {
    return {
      value: {
        [newRuleOperator]: pathData,
      },
      length: pathData.length,
    };
  }
  const newPathString = `${parentPathString}${parentPathString.length ? '.' : ''}${newRuleOperator}`;

  deleteProperty(
    booleanRule,
    parentPathString,
  );

  const updatedRule = setProperty(
    booleanRule,
    newPathString,
    pathData,
  );

  return {
    value: cleanRulesWithTopLevelOperator(updatedRule),
    length: pathData.length,
  };
}

/**
 * A function called in a specific scenario where all other criterias were not met,
 * and it's deemed required to update bool operator of the top level rule object
 * It uses parent path array's first item to locate the rule
 */
function changeOperatorInRuleObjectUsingParentPath({
  newRuleOperator,
  booleanRule,
  parentPathArray,
  pathToPerformActionAt,
  ambiguousRuleOperatorUsed,
}: {
  newRuleOperator: 'and' | 'or';
  booleanRule: Rules | {};
  parentPathArray: string[];
  pathToPerformActionAt: string;
  ambiguousRuleOperatorUsed: boolean;
}) {
  const newValue = booleanRule[parentPathArray[0]];
  const operationObject = newValue?.length > 2
    ? buildRuleOperatorChangeOperationMsg({
      ambiguousRuleOperatorUsed,
      pathToPerformActionAt,
    })
    : undefined;
  return {
    value: cleanRulesWithTopLevelOperator({
      [newRuleOperator]: booleanRule[parentPathArray[0]],
    }),
    operation: operationObject,
  };
}

export function correctAmbiguousBooleanRule({
  newRuleOperatorForCorrection,
  originalRule,
}: {
  newRuleOperatorForCorrection: 'and' | 'or';
  originalRule: Rules | {};
}) {
  const booleanRule = cloneDeep(originalRule);

  // these 2 handlings are just there for caution/satifying TS compiler, not really going to happen.
  if (typeof booleanRule === 'string') return { value: booleanRule };
  if ('id' in booleanRule) return booleanRule;

  let topLevelOperator;
  if ('and' in booleanRule) {
    topLevelOperator = 'and';
  } else if ('or' in booleanRule) {
    topLevelOperator = 'or';
  }

  if (!(topLevelOperator in booleanRule)) return booleanRule;
  return correctAmbiguousBooleanRuleObject(
    booleanRule as BooleanRule,
    newRuleOperatorForCorrection,
  );
}

export function toggleAndOrInBooleanRule({
  newRuleOperator,
  pathToPerformActionAt,
  originalRule,
}: {
  newRuleOperator: 'and' | 'or';
  pathToPerformActionAt: string;
  originalRule: Rules | {};
}) {
  const booleanRule = cloneDeep(originalRule);
  if (typeof booleanRule === 'string') return { value: booleanRule };
  if ('id' in booleanRule) {
    return {
      operation: buildActionRuleThresholdOperationMsg({
        publicId: '',
        message: RuleUpdateErrorType.NO_GROUPABLE_PREVIOUS_ITEM,
        data: undefined,
      }),
    };
  }

  const ambiguousRuleOperatorUsed = checkIfRuleHasAmbiguousKey(cloneDeep(originalRule));

  // the path is like this or[2].and
  // this means it's nested and/or group and user wants to change that operator
  // so we call changeOperatorInRuleObject to try flip the operator at that path directly
  // and reformat any surrounding threshold/nested rule objects accordingly as a consequence/side effect
  if (pathToPerformActionAt.endsWith('and') || pathToPerformActionAt.endsWith('or')) {
    const { value: updatedBooleanRuleWithNewOperator, length } = changeOperatorInRuleObject({
      newRuleOperator,
      pathToPerformActionAt,
      booleanRule: booleanRule as BooleanRule | BooleanRuleArray,
    });

    const operationObject = length > 2
      ? buildRuleOperatorChangeOperationMsg({
        ambiguousRuleOperatorUsed,
        pathToPerformActionAt,
      })
      : undefined;

    return {
      value: cleanRulesWithTopLevelOperator(updatedBooleanRuleWithNewOperator),
      operation: operationObject,
    };
  }

  // otherwise...
  // path ended with index ([n])
  const pathData = cloneDeep(getProperty(
    booleanRule,
    pathToPerformActionAt,
  ) as BooleanRule | IndexedRule | BooleanRuleArray);

  const {
    pathAsArray: parentPathArray,
    parentPathString,
    lastPathItem,
  } = getPathArrayAndParentPathString({
    pathString: pathToPerformActionAt.slice(),
    parentLevel: 1,
  });

  const parentPathData = cloneDeep(getProperty(
    booleanRule,
    parentPathString,
  )) as (BooleanRule | IndexedRule)[] | BooleanRule | IndexedRule;

  // parent path should be "and.[2].or" or something like that, where
  // it should end with either and/or in this case
  // we grab the last index in the parentPathInArray to get that operator string
  if (!Array.isArray(parentPathData) || !parentPathData) {
    return {
      operation: buildActionRuleThresholdOperationMsg({
        publicId: '',
        message: RuleUpdateErrorType.INCORRECT_PARENT_RULE_ARRAY,
        data: undefined,
      }),
    };
  }

  // we are trying to grab two items in the parent array;
  // one that is last path was pointing to,
  // and one that is the only on the previous index of the last path
  const previousIndexItemFromParentPath = parentPathData[lastPathItem > 0 ? lastPathItem - 1 : 0];
  if (!previousIndexItemFromParentPath) {
    return {
      operation: buildActionRuleThresholdOperationMsg({
        publicId: '',
        message: RuleUpdateErrorType.NO_GROUPABLE_PREVIOUS_ITEM,
        data: undefined,
      }),
    };
  }

  const ruleOperatorOfPreviousIndexItemFromParent = getOperatorUsedInRuleObject({
    rule: previousIndexItemFromParentPath,
  });

  if (parentPathArray.length === 1
    && !Array.isArray(previousIndexItemFromParentPath)
    && !!ruleOperatorOfPreviousIndexItemFromParent
    && !Array.isArray(pathData)
    && !!getOperatorUsedInRuleObject({
      rule: pathData,
    })
  ) {
    // this is top level bool operator change
    const newRule = {
      [newRuleOperator]: parentPathData,
    };
    return { value: cleanRulesWithTopLevelOperator(newRule) };
  }
  // either previousIndexItemFromParentPath was not { id: string }
  // AND it was another nested and/or rule array
  const ruleOperatorOfPathData = getOperatorUsedInRuleObject({
    rule: pathData as IndexedRule | BooleanRule,
  });

  let newGroupedValue;
  const originalPathIndices = getNumericPathIndices(pathToPerformActionAt, 'rules');
  if (ruleOperatorOfPathData) {
    const pathDataUnderRuleOperator = (pathData as BooleanRuleArray)[ruleOperatorOfPathData];
    // we check if previous one is not an array
    // and it's pointing to either and / or object
    // and if pathDataUnderRuleOperator is same as either and/or object living in the prev
    // we just assign one of them as the new grouped value
    // note: this logic is largely dormant since the grouping behavior overhaul
    if (!Array.isArray(previousIndexItemFromParentPath)
      && previousIndexItemFromParentPath[ruleOperatorOfPathData]
      && isEqual(previousIndexItemFromParentPath[ruleOperatorOfPathData], pathDataUnderRuleOperator)
    ) {
      newGroupedValue = cloneDeep(previousIndexItemFromParentPath[ruleOperatorOfPathData]);
    } else {
      // if original path indices are just 2 items in it, we need to flip the entire top level bool operator
      if (originalPathIndices.length === 2) {
        return changeOperatorInRuleObjectUsingParentPath({
          newRuleOperator,
          booleanRule,
          parentPathArray,
          pathToPerformActionAt,
          ambiguousRuleOperatorUsed,
        });
      }

      // otherwise, we build a new sub group with a new operator and insert it where the path was at
      setProperty(
        booleanRule,
        pathToPerformActionAt,
        { [newRuleOperator]: (pathData as BooleanRuleArray)[ruleOperatorOfPathData] },
      ) as BooleanRule;

      return { value: cleanRulesWithTopLevelOperator(booleanRule) };
    }
  } else if (!('id' in previousIndexItemFromParentPath)) {
    // prev item is and: [] / or: []
    // and if original path indices are just 2 items in it, we need to flip the entire top level bool operator
    if (originalPathIndices.length === 2) {
      return changeOperatorInRuleObjectUsingParentPath({
        newRuleOperator,
        booleanRule,
        parentPathArray,
        pathToPerformActionAt,
        ambiguousRuleOperatorUsed,
      });
    }

    return {
      value: cleanRulesWithTopLevelOperator({
        [newRuleOperator]: parentPathData,
      }),
    };
  } else {
    // path data was not and/or object, so we can just safely combine them into a new group
    newGroupedValue = cloneDeep([
      previousIndexItemFromParentPath,
      pathData,
    ]);
  }

  // at this point, we can safely create a new group using newGroupedValue
  parentPathData[lastPathItem] = {
    [newRuleOperator]: newGroupedValue,
  } as BooleanRule;
  if (lastPathItem > 0 || Array.isArray(previousIndexItemFromParentPath)) {
    parentPathData.splice(lastPathItem - 1, 1);
  }

  setProperty(
    booleanRule,
    parentPathString,
    parentPathData,
  ) as BooleanRule;

  const parentDataAfterUpdate = compact(getProperty(
    booleanRule,
    parentPathString,
  ) as Rules[] | IndexedRule[]);

  // check if what's inside parentData is now just one item that's newly grouped
  // then this parent has no use
  // and we want to bump the nested item as the parent and get rid of the OG parent { "and|or": [...] }
  if (parentDataAfterUpdate.length === 1) {
    const {
      parentPathString: parentRulePathString,
    } = getPathArrayAndParentPathString({
      pathString: parentPathString.slice(),
      parentLevel: 1,
    });
    const newPathString = `${parentRulePathString}${parentRulePathString.length ? '.' : ''}${newRuleOperator}`;
    const newValue = Array.isArray(parentDataAfterUpdate[0])
      ? parentDataAfterUpdate[0]
      : parentDataAfterUpdate[0][newRuleOperator];

    setProperty(
      booleanRule,
      newPathString,
      newValue,
    );

    if (newPathString !== parentPathString) {
      deleteProperty(
        booleanRule,
        parentPathString,
      );
    }

    return { value: cleanRulesWithTopLevelOperator(booleanRule) };
  }

  return { value: cleanRulesWithTopLevelOperator(booleanRule) };
}

export function insertThresholdToBooleanRule({
  insertOrder,
  pathToPerformActionAt,
  originalRule,
  thresholdId,
}: {
  insertOrder: NewLineInsertionPosition;
  pathToPerformActionAt: string;
  originalRule: Rules | {};
  thresholdId: string;
}) {
  const booleanRule = cloneDeep(originalRule);
  if (typeof booleanRule === 'string') return booleanRule;

  const newThresholdObject = { id: thresholdId };

  if ('id' in booleanRule) {
    // this was a simple flat indexed rule with 1 threshold
    return {
      [booleanRuleStrings.AND_OPERATOR_STRING]: (insertOrder === NewLineInsertionPosition.ABOVE) ? [
        newThresholdObject,
        {
          id: booleanRule.id,
        },
      ] : [
        {
          id: booleanRule.id,
        },
        newThresholdObject,
      ],
    };
  }

  const firstOperatorKeyInTheOriginalRule = 'and' in booleanRule
    ? booleanRuleStrings.AND_OPERATOR_STRING
    : booleanRuleStrings.OR_OPERATOR_STRING;
  // otherwise it has more than one threshold in the rule
  if (pathToPerformActionAt?.includes('[')) {
    // event was sent to a path that's more than simply and/or operator key,
    // meaning this threshold would have to be attached to a specific key value pair
    // path would be like this: sample 1- and[1] / sample 2- or[2].and[0]

    // get parent level data
    const {
      parentPathString,
      lastPathItem,
    } = getPathArrayAndParentPathString({
      pathString: pathToPerformActionAt,
      parentLevel: 1,
    });

    const parentLevelData = getProperty(
      booleanRule,
      parentPathString,
    );

    const ruleOperatorOfParentData = getOperatorUsedInRuleObject({
      rule: parentLevelData as IndexedRule | BooleanRule,
    });

    let updatedRule;
    if (ruleOperatorOfParentData && insertOrder === NewLineInsertionPosition.BELOW) {
      // if these two conditions met, it means user clicked from `(` group starting parenthesis line
      // and also want to add something below that line, meaning we should add new one as a first item in there
      (parentLevelData as Rules[])[ruleOperatorOfParentData].splice(0, 0, newThresholdObject);
      updatedRule = parentLevelData;
    } else if (Array.isArray(parentLevelData)) {
      // we insert new one in between
      // key thing is that RuleLines.tsx will send a correct path string depending on the user action (above or below)
      // so that this logic can always be executed in this fashion. refer to that file for more.
      updatedRule = [
        ...(parentLevelData as Rules[]).slice(0, lastPathItem),
        { id: thresholdId },
        ...(parentLevelData as Rules[]).slice(lastPathItem),
      ];
    } else {
      updatedRule = {
        and: [
          parentLevelData,
          { id: thresholdId },
        ],
      };
    }

    setProperty(
      booleanRule,
      parentPathString,
      updatedRule,
    );

    return booleanRule;
  }

  return {
    [firstOperatorKeyInTheOriginalRule]: [
      {
        id: thresholdId,
      },
      ...booleanRule[firstOperatorKeyInTheOriginalRule],
    ],
  };
}
